/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core';
import { runElkLayout, ElkPoint } from '../networkPolicy/elkLayout';
import { KgDoc, KgEdge, KgNode, edgesOf } from './types';

// Entity-altitude fleet view (plan phase-3 §3.1). Rendering follows the
// network-topology rules from AGENTS.md: ELK owns placement AND
// routes (static polylines, nothing draggable), the bundled elk build is
// lazy-loaded through the shared helper, and ELK is fed DOM-measured node
// sizes — nodes render hidden first, are measured, then laid out.
//
// Edge semantics are the product: solid = declared+observed, dashed =
// observed-only (undeclared dependency), red = declared-but-not-observed
// (stale declaration). Edges without a flag (fixtures predating the join)
// render neutral. Severity is never color-alone: the dash pattern carries
// the distinction too.

const NODE_MAX_W = 220;

type Positioned = {
  positions: Map<string, ElkPoint>;
  routes: Map<string, ElkPoint[]>;
  width: number;
  height: number;
};

const useStyles = makeStyles(theme => ({
  canvasWrap: {
    position: 'relative',
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    background: theme.palette.background.default,
    height: '70vh',
  },
  node: {
    position: 'absolute',
    padding: theme.spacing(0.5, 1),
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    fontSize: 12,
    lineHeight: 1.3,
    maxWidth: NODE_MAX_W,
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  kind: {
    display: 'block',
    fontSize: 10,
    color: theme.palette.text.secondary,
  },
  measureLayer: {
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    top: 0,
    left: 0,
  },
  legend: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
    fontSize: 12,
    marginBottom: theme.spacing(1),
  },
}));

const edgeId = (e: KgEdge, i: number) => `e${i}-${e.source}->${e.target}`;

const NodeBody = ({ n, classes }: { n: KgNode; classes: { kind: string } }) => (
  <>
    <span className={classes.kind}>{n.entity_kind ?? 'node'}</span>
    {n.label ?? n.id}
  </>
);

const edgeStyle = (
  e: KgEdge,
  palette: { neutral: string; observed: string; stale: string },
) => {
  // flag=declared means "declared but NOT observed" once the join runs —
  // the stale-declaration class. both = confirmed. observed = undeclared.
  if (e.flag === 'observed') {
    return { stroke: palette.observed, dash: '6 4', label: 'observed-only' };
  }
  if (e.flag === 'declared') {
    return { stroke: palette.stale, dash: '2 3', label: 'declared-not-observed' };
  }
  return { stroke: palette.neutral, dash: undefined, label: e.flag ?? 'edge' };
};

export const FleetGraph = (props: { doc: KgDoc; hideStubs: boolean }) => {
  const classes = useStyles();
  const theme = useTheme();
  const measureRefs = useRef(new Map<string, HTMLDivElement>());
  const [layout, setLayout] = useState<Positioned | null>(null);
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const [measureTick, setMeasureTick] = useState(0);

  const { nodes, edges } = useMemo(() => {
    const all = props.doc.nodes;
    const wanted = props.hideStubs
      ? all.filter(n => !(n.tags ?? []).includes('needs-catalog-info'))
      : all;
    const ids = new Set(wanted.map(n => n.id));
    const es = edgesOf(props.doc).filter(
      e => ids.has(String(e.source)) && ids.has(String(e.target)),
    );
    return { nodes: wanted, edges: es };
  }, [props.doc, props.hideStubs]);

  // Measure pass: hidden nodes render first; once every ref exists we read
  // offset sizes and hand them to ELK. Node-set changes re-trigger via the
  // tick (ref callbacks fire before layout effects).
  useEffect(() => {
    let cancelled = false;
    const dims = measureRefs.current;
    const ready = nodes.every(n => dims.has(n.id));
    if (!ready) {
      // Refs land synchronously after render; a tick re-runs this effect.
      const t = setTimeout(() => setMeasureTick(x => x + 1), 50);
      return () => clearTimeout(t);
    }
    (async () => {
      try {
        const res = await runElkLayout({
          nodes: nodes.map(n => {
            const el = dims.get(n.id)!;
            return {
              id: n.id,
              width: Math.min(el.offsetWidth + 2, NODE_MAX_W),
              height: el.offsetHeight + 2,
            };
          }),
          edges: edges.map((e, i) => ({
            id: edgeId(e, i),
            source: String(e.source),
            target: String(e.target),
          })),
        });
        if (cancelled) return;
        let width = 0;
        let height = 0;
        res.positions.forEach((p, id) => {
          const el = dims.get(id);
          width = Math.max(width, p.x + (el?.offsetWidth ?? NODE_MAX_W) + 40);
          height = Math.max(height, p.y + (el?.offsetHeight ?? 32) + 40);
        });
        setLayout({ ...res, width, height });
        setLayoutError(null);
      } catch (e) {
        // Chunk-load failure: elkLayout already cleared its import memo, so
        // Retry (the refresh cycle re-running this effect) can succeed.
        if (!cancelled) setLayoutError(String((e as Error).message ?? e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nodes, edges, measureTick]);

  const palette = {
    neutral: theme.palette.text.disabled,
    observed: theme.palette.primary.main,
    stale: theme.palette.error.main,
  };

  return (
    <div>
      <div className={classes.legend} role="list" aria-label="edge legend">
        {(
          [
            ['declared+observed / unflagged', palette.neutral, undefined],
            ['observed-only (undeclared)', palette.observed, '6 4'],
            ['declared-not-observed (stale)', palette.stale, '2 3'],
          ] as const
        ).map(([label, stroke, dash]) => (
          <span role="listitem" key={label}>
            <svg width={26} height={8} aria-hidden style={{ marginRight: 4 }}>
              <line x1={1} y1={4} x2={25} y2={4} stroke={stroke} strokeWidth={1.5} strokeDasharray={dash} />
            </svg>
            {label}
          </span>
        ))}
        <span role="listitem">{nodes.length} nodes · {edges.length} edges</span>
      </div>
      {layoutError && (
        <div role="alert">layout failed: {layoutError} — retrying on next refresh</div>
      )}
      <div className={classes.canvasWrap}>
        {/* Hidden measure layer: every node renders here first so ELK gets real sizes. */}
        <div className={classes.measureLayer} aria-hidden>
          {nodes.map(n => (
            <div
              key={`m-${n.id}`}
              className={classes.node}
              style={{ position: 'static', display: 'inline-block' }}
              ref={el => {
                if (el) measureRefs.current.set(n.id, el);
                else measureRefs.current.delete(n.id);
              }}
            >
              <NodeBody n={n} classes={classes} />
            </div>
          ))}
        </div>
        {layout && (
          <>
            <svg
              width={layout.width}
              height={layout.height}
              style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
            >
              {edges.map((e, i) => {
                const pts = layout.routes.get(edgeId(e, i));
                if (!pts) return null;
                const s = edgeStyle(e, palette);
                return (
                  <polyline
                    key={edgeId(e, i)}
                    points={pts.map(p => `${p.x},${p.y}`).join(' ')}
                    fill="none"
                    stroke={s.stroke}
                    strokeWidth={1.2}
                    strokeDasharray={s.dash}
                  >
                    <title>
                      {`${e.source} —${e.relation ?? 'edge'}${e.flag ? ` (${s.label})` : ''}→ ${e.target}`}
                    </title>
                  </polyline>
                );
              })}
            </svg>
            {nodes.map(n => {
              const p = layout.positions.get(n.id);
              if (!p) return null;
              return (
                <div
                  key={n.id}
                  className={classes.node}
                  style={{ left: p.x, top: p.y }}
                  title={`${n.label ?? n.id}${n.system ? ` · system:${n.system}` : ''}${n.owner ? ` · ${n.owner}` : ''}`}
                >
                  <NodeBody n={n} classes={classes} />
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};
